import { useMicList } from "@/features/Microphone/hooks/useMicList";
import { MicrophoneError } from "@/features/Microphone/types";
import { appLogger } from "@/utils/AppLogger";
import { useCallback, useEffect, useRef, useState } from "react";

export interface UseMicrophoneProps {
  defaultSelected?: string;
  startOnMount: boolean;
  switchOnDisconnection: boolean;
}

export function useMicrophone(props?: UseMicrophoneProps | undefined) {
  const [selectedMicrophone, setSelectedMicrophone] = useState<MediaDeviceInfo | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<MicrophoneError | null>(null);

  const hasStarted = useRef(false);

  const { defaultSelected, startOnMount, switchOnDisconnection } = props || {};

  const selectedMicrophoneRef = useRef(selectedMicrophone);
  selectedMicrophoneRef.current = selectedMicrophone;

  const streamRef = useRef(stream);
  streamRef.current = stream;

  const { error: micListError, devices, startDevicesListener, getMicrophoneDevices, ...rest } = useMicList();

  const onStopMicrophone = useCallback(async () => {
    try {
      if (!streamRef.current) return;
      streamRef.current.getTracks().forEach((track) => track.stop());
      setStream(null);
      streamRef.current = null;

      appLogger(`MICROPHONE - Removing Stream`);
      appLogger(`MICROPHONE - Disconnected Mic ${selectedMicrophoneRef.current?.label}`);
    } catch (error) {
      appLogger(`MICROPHONE - Error stopping Microphone: ${error}`);
    }
  }, []);

  const onStartMicrophone = useCallback(
    async (deviceId: string, constraints?: MediaTrackConstraints) => {
      try {
        const allDevices = await getMicrophoneDevices();
        const selected = allDevices.find((d) => d.deviceId === deviceId);

        if (!selected) {
          throw new Error(`Selected microphone not available: ${deviceId}`);
        }

        const newStream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            ...constraints,
            deviceId: { exact: selected?.deviceId },
          },
        });

        newStream.getAudioTracks()[0].onended = async () => {
          await onStopMicrophone();
        };

        if (streamRef.current) await onStopMicrophone();

        streamRef.current = newStream;
        setStream(newStream);
        const isActive = newStream.getAudioTracks()[0]?.readyState === "live";
        appLogger(`[MICROPHONE] Connected: ${selected?.label} (active: ${isActive})`, { type: "info" });
        setError(null);
        return newStream;
      } catch (error) {
        appLogger(`MICROPHONE - Error starting Microphone: ${error}`);
        return null;
      }
    },
    [getMicrophoneDevices, onStopMicrophone],
  );
  const onSelectMicrophone = useCallback(
    async (selected: string, constraints?: MediaTrackConstraints) => {
      if (streamRef.current && selectedMicrophoneRef.current?.deviceId === selected) return null;
      const allDevices = await getMicrophoneDevices();

      if (!allDevices.length) {
        appLogger(`[MICROPHONE] No devices available on device list`);
      }
      appLogger(`[MICROPHONE] Microphones available: ${allDevices.length}`);

      const selectedDevice = allDevices.find((d) => d.deviceId === selected);

      if (!selectedDevice) {
        appLogger(`[MICROPHONE] device not available for ${selected}`);
      }

      if (selectedDevice) {
        setSelectedMicrophone(selectedDevice);
        return await onStartMicrophone(selectedDevice.deviceId, constraints);
      } else if (allDevices[0]?.deviceId) {
        setSelectedMicrophone(allDevices[0]);
        return await onStartMicrophone(allDevices[0]?.deviceId, constraints);
      }
      return null;
    },
    [getMicrophoneDevices, onStartMicrophone],
  );

  const OnDisconnectedSelectedMicrophone = useCallback(async () => {
    if (!streamRef.current && devices[0]?.value !== "no-device" && !selectedMicrophoneRef?.current) return;
    const currentMicrophoneWasDisconnected = !devices?.find((m) => m.deviceId === selectedMicrophoneRef.current?.deviceId);
    if (currentMicrophoneWasDisconnected) {
      await onSelectMicrophone(devices[0]?.deviceId);
    }
  }, [devices, onSelectMicrophone]);

  useEffect(() => {
    if (!switchOnDisconnection) return;
    OnDisconnectedSelectedMicrophone();
  }, [OnDisconnectedSelectedMicrophone, switchOnDisconnection]);

  useEffect(() => {
    if (!startOnMount) return;
    if (!stream && !selectedMicrophone && defaultSelected && !hasStarted.current && devices.length && devices[0]?.value !== "no-device") {
      hasStarted.current = true;
      onSelectMicrophone(defaultSelected);
    }
  }, [startOnMount, onSelectMicrophone, selectedMicrophone, defaultSelected, stream, devices]);

  useEffect(() => {
    return () => {
      onStopMicrophone();
    };
  }, [onStopMicrophone]);

  return {
    error: error || micListError,
    stream,
    devices,
    selectedMicrophone,
    onStartMicrophone,
    onStopMicrophone,
    onSelectMicrophone,
    startDevicesListener,
    OnDisconnectedSelectedMicrophone,
    ...rest,
  };
}
